/**
 * 优雅降级
 * 服务不可用时客户端本地回退策略
 */
export interface FallbackHandler {
  command: string
  handler: (params?: any) => Promise<any>
  condition?: 'disconnected' | 'timeout' | 'error' | 'always'
}

export interface DegradationOptions {
  /** 超时后降级(ms)，默认 5000 */
  timeout?: number
  /** 是否启用，默认 true */
  enabled?: boolean
}

export class GracefulDegradation {
  private fallbacks = new Map<string, FallbackHandler>()
  private options: Required<DegradationOptions>
  private degradedCount = 0
  private isOnline = true

  constructor(options?: DegradationOptions) {
    this.options = {
      timeout: options?.timeout ?? 5000,
      enabled: options?.enabled ?? true,
    }
  }

  /** 注册回退处理器 */
  register(handler: FallbackHandler): void {
    this.fallbacks.set(handler.command, handler)
  }

  /** 注销回退处理器 */
  unregister(command: string): void {
    this.fallbacks.delete(command)
  }

  /** 尝试执行（如果条件满足则降级） */
  async tryExecute<T>(
    command: string,
    remoteFn: () => Promise<T>,
    params?: any,
  ): Promise<{ result: T; degraded: boolean }> {
    if (!this.options.enabled) return { result: await remoteFn(), degraded: false }

    const fallback = this.fallbacks.get(command)
    if (!fallback) return { result: await remoteFn(), degraded: false }

    if (this.shouldDegrade(fallback.condition)) {
      this.degradedCount++
      const result = await fallback.handler(params) as T
      return { result, degraded: true }
    }

    try {
      const result = await Promise.race([
        remoteFn(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), this.options.timeout),
        ),
      ])
      return { result, degraded: false }
    } catch {
      if (fallback) {
        this.degradedCount++
        const result = await fallback.handler(params) as T
        return { result, degraded: true }
      }
      throw new Error(`指令 ${command} 执行失败且无降级策略`)
    }
  }

  /** 设置在线状态 */
  setOnline(online: boolean): void {
    this.isOnline = online
  }

  /** 获取降级次数 */
  get degradedExecutions(): number {
    return this.degradedCount
  }

  /** 获取已注册降级指令列表 */
  listFallbacks(): string[] {
    return [...this.fallbacks.keys()]
  }

  /** 重置计数 */
  resetStats(): void {
    this.degradedCount = 0
  }

  private shouldDegrade(condition?: string): boolean {
    if (condition === 'always') return true
    if (condition === 'disconnected' && !this.isOnline) return true
    return false
  }
}
